import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import { useGetThirdPartyById } from 'src/hooks/queries/third-party/useGetThirdPartyById';
import {
  getContributors,
  getOwners,
  getViewers,
} from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useUpdateThirdParty } from '@/hooks/mutations/third-party';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

import { ThirdPartyForm } from '../../../forms/ThirdPartyForm';
import type { ThirdPartyFormData } from '../../../forms/thirdPartySchema';
import { defaultValues } from '../../../forms/thirdPartySchema';

const DetailsTab: FC = () => {
  useI18NSummaryHelpContent('third_party.help');

  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');

  const formPrefillEnabled = useIsFormPrefillEnabled();
  const navigate = useNavigate();

  const thirdPartyId = useGetGuidParam('id');
  const { data, loading: loadingThirdParty } = useGetThirdPartyById({
    queryArgs: { thirdPartyId },
  });

  const thirdParty = data?.third_party;

  const { hasPermission: canEdit, loading: canEditLoading } =
    useHasPermissionQuery('update:third_party', thirdParty);

  const { updateThirdParty } = useUpdateThirdParty();
  const { updateFiles } = useMultiParentFileUpdate();

  const onSave = async (formData: ThirdPartyFormData) => {
    if (!data?.third_party?.Id) {
      return;
    }
    const { files } = formData;

    await updateThirdParty({
      Id: data.third_party.Id,
      // Optimistic-concurrency token: the snapshot this form was loaded from.
      // A second editor saving over a newer row is rejected with a 409 rather
      // than silently winning (COI-622).
      OriginalTimestamp: data.third_party.ModifiedAtTimestamp,
      Title: formData.title,
      Description: formData.description,
      CompanyName: formData.companyName,
      CompaniesHouseNumber: formData.companiesHouseNumber,
      Address: formData.address,
      CityTown: formData.cityTown,
      Postcode: formData.postcode,
      Country: formData.country,
      PrimaryContactName: formData.primaryContactName,
      ContactName: formData.contactName,
      ContactEmail: formData.contactEmail,
      CompanyDomain: formData.companyDomain,
      Type: formData.type,
      Status: formData.status,
      Criticality: formData.criticality,
      CustomAttributeData: formData.CustomAttributeData,
      TagTypeIds: formData.tags?.map((t) => t.TagTypeId) || [],
      DepartmentTypeIds:
        formData.departments?.map((d) => d.DepartmentTypeId) || [],
      ...ownerContributorAndViewerIds(formData),
    });
    await updateFiles({
      parentType: Parent_Type_Enum.ThirdParty,
      parentIds: [data.third_party.Id],
      originalFiles: data?.third_party?.files.map((f) => f.file) ?? [],
      selectedFiles: files,
    });
  };

  const onDismiss = () => {
    navigate(-1);
  };

  if (!thirdParty) {
    return null;
  }

  return (
    <ThirdPartyForm
      readOnly={!canEdit || canEditLoading || loadingThirdParty}
      values={{
        ...defaultValues,
        title: thirdParty.Title,
        description: thirdParty.Description,
        companyName: thirdParty.CompanyName,
        companiesHouseNumber: thirdParty.CompaniesHouseNumber,
        address: thirdParty.Address,
        cityTown: thirdParty.CityTown,
        postcode: thirdParty.Postcode,
        country: thirdParty.Country,
        primaryContactName: thirdParty.PrimaryContactName,
        contactName: thirdParty.ContactName,
        contactEmail: thirdParty.ContactEmail,
        companyDomain: thirdParty.CompanyDomain,
        type: thirdParty.Type,
        status: thirdParty.Status,
        criticality: String(thirdParty.Criticality) as unknown as number,
        Owners: getOwners(thirdParty),
        Contributors: getContributors(thirdParty),
        Viewers: getViewers(thirdParty),
        ancestorContributors: thirdParty.ancestorContributors,
        tags: thirdParty.tags,
        departments: thirdParty.departments,
        CustomAttributeData: thirdParty.CustomAttributeData,
        files: thirdParty.files.map((f) => f.file),
      }}
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
      activeFormRecordId={thirdPartyId}
    />
  );
};

export default DetailsTab;
